import { useElementRendered } from "helpers/DomHelper";
import { changeShowChangeEntityDialog } from "modules/auth/store";
import { logOff } from "modules/auth/store/signin/thunks";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { RootState } from "store";
import { CHANGE_NIAS_ENTITY_ROUTE_START } from "../NiasEntityChange";
import { changeNiasEntityRouteParameters } from "services";
import { entityChangeCache } from "modules/auth/cache";
import { getEnvironmentBasedValue, getEPoljoprivredaUrl } from "helpers/EnvironmentHelper";
import { AppRoutes } from "routing";

type AccessibilityStyles = {
    fontSize: number;
    dyslexia: boolean;
    lowVision: boolean;
};

type StateUpdater = React.Dispatch<React.SetStateAction<AccessibilityStyles>>;

const loadScript = (src: URL) => {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.id = "nias-header";
    script.src = src.toString();
    script.defer = true;
    document.body.appendChild(script);
};

const decreaseFontSize = (setAccessibilityStyles: StateUpdater) => {
    setAccessibilityStyles(acc => {
        const newFontSize = Math.max(acc.fontSize - 5, 75);
        return { ...acc, fontSize: newFontSize };
    });
};

const increaseFontSize = (setAccessibilityStyles: StateUpdater) => {
    setAccessibilityStyles(acc => {
        const newFontSize = Math.min(acc.fontSize + 5, 115);
        return { ...acc, fontSize: newFontSize };
    });
};

const resetFontSize = (setAccessibilityStyles: StateUpdater) => {
    setAccessibilityStyles(acc => ({ ...acc, fontSize: 95 }));
};

const setDyslexia = (setAccessibilityStyles: StateUpdater, active: boolean) => {
    setAccessibilityStyles(acc => ({ ...acc, dyslexia: active, lowVision: false }));
};

const lowVis = (setAccessibilityStyles: StateUpdater, active: boolean) => {
    setAccessibilityStyles(acc => ({ ...acc, lowVision: active, dyslexia: false }));
};

const useNiasHeaderRendered = () => useElementRendered("nav#shell");
const useEntityChangeDialogRendered = () => useElementRendered(".mfp-wrap");

const PRODUCTION_URL = "https://eusluge-nav.gov.hr/e_gradani.aspx";
const NON_PRODUCTION_URL = "https://eusluge-nav-test.gov.hr/e_gradani.aspx";

export const NiasHeader = () => {
    const storedStateString: AccessibilityStyles = JSON.parse(
        localStorage.getItem("accessibility") ?? "null"
    );

    const dispatch = useDispatch();
    const location = useLocation();

    const [accessibilityStyles, setAccessibilityStyles] = useState<AccessibilityStyles>({
        fontSize: storedStateString?.fontSize ?? 95,
        dyslexia: storedStateString?.dyslexia ?? false,
        lowVision: storedStateString?.lowVision ?? false,
    });

    const { user, signupNiasData } = useSelector((state: RootState) => state.signIn);
    const niasReady = useNiasHeaderRendered();
    const entityChangeDialogRendered = useEntityChangeDialogRendered();

    const scriptSource = useMemo((): string | undefined => {
        let src = new URL(getEnvironmentBasedValue(PRODUCTION_URL, NON_PRODUCTION_URL));

        src.searchParams.append("logout_url", `${getEPoljoprivredaUrl()}signout/0`);
        src.searchParams.append(
            "change_entity_url",
            `${CHANGE_NIAS_ENTITY_ROUTE_START}${changeNiasEntityRouteParameters}`
        );

        if (!user) {
            if (location.pathname !== AppRoutes.SignUp) {
                return undefined;
            }
            const params = new URLSearchParams(window.location?.search);
            const hasTokenInUrl = params.has("token");
            
            if (hasTokenInUrl && !signupNiasData) {
                return undefined;
            }

            if (signupNiasData) {
                const { navToken, messageId, forPersonOib, forLegalIps, toLegalIps, toLegalIzvorReg, forLegalIzvorReg, oib } = signupNiasData;
                
                if (navToken && messageId) {
                    src.searchParams.append("navToken", navToken);
                    src.searchParams.append("messageId", messageId);
                }
                
                if (forPersonOib) src.searchParams.append("ForPersonOib", forPersonOib);
                if (forLegalIps) src.searchParams.append("ForLegalIps", forLegalIps);
                if (toLegalIps) src.searchParams.append("ToLegalIps", toLegalIps);
                if (toLegalIzvorReg) src.searchParams.append("ToLegalIzvor_reg", toLegalIzvorReg);
                if (forLegalIzvorReg) src.searchParams.append("ForLegalIzvor_reg", forLegalIzvorReg);
                
                if (!forPersonOib && !forLegalIps && !toLegalIps && oib) {
                    src.searchParams.append("ForPersonOib", oib);
                }
            }

            src.searchParams.append("show_entities", String(true));
            src.searchParams.append("show_persons", String(true));
            src.searchParams.append("language", "hr");

            return src.toString();
        }

        const entityChange = entityChangeCache().get();
        const changeRequest = entityChange?.request;

        let forPersonOib,
            forLegalIps,
            toLegalIps,
            toLegalIzvorReg,
            forLegalIzvorReg: string | undefined;
        if (changeRequest) {
            forPersonOib = changeRequest.forPersonOib;
            forLegalIps = changeRequest.forLegalIps;
            toLegalIps = changeRequest.toLegalIps;
            toLegalIzvorReg = changeRequest.toLegalIzvorReg;
            forLegalIzvorReg = changeRequest.forLegalIzvorReg;
        } else {
            forPersonOib = user?.forPersonOib;
            forLegalIps = user?.forLegalIps;
            toLegalIps = user?.toLegalIps;
            toLegalIzvorReg = user?.toLegalIzvorReg;
            forLegalIzvorReg = user?.forLegalIzvorReg;
        }
        const hasNiasData =
            forPersonOib || forLegalIps || toLegalIps || toLegalIzvorReg || forLegalIzvorReg;

        const navToken = user?.navToken;
        const messageId = user?.messageId;

        if (!hasNiasData && !(navToken && messageId)) return;

        const showChangeEntityDialog = user?.showChangeEntityDialog;

        if (navToken && messageId) {
            src.searchParams.append("navToken", navToken);
            src.searchParams.append("messageId", messageId);
        }

        forPersonOib && src.searchParams.append("ForPersonOib", forPersonOib);
        forLegalIps && src.searchParams.append("ForLegalIps", forLegalIps);
        toLegalIps && src.searchParams.append("ToLegalIps", toLegalIps);
        toLegalIzvorReg && src.searchParams.append("ToLegalIzvor_reg", toLegalIzvorReg);
        forLegalIzvorReg && src.searchParams.append("ForLegalIzvor_reg", forLegalIzvorReg);

        let appendedOib = false;
        const oib = user?.oib;
        if (oib && !forPersonOib && !forLegalIps && !toLegalIps) {
            src.searchParams.append("ForPersonOib", oib);
            appendedOib = true;
        }

        src.searchParams.append("show_entities", String(true));
        src.searchParams.append("show_persons", String(true));
        src.searchParams.append("language", "hr");
        if (!appendedOib && showChangeEntityDialog && oib)
            src.searchParams.append("ForPersonOib", oib);

        return src.toString();
    }, [user, signupNiasData]);

    useEffect(() => {
        if (!scriptSource) {
            document.getElementById("nias-header")?.remove();
            document.getElementById("egradjani")?.remove();
            return;
        }

        const existingScript = document.getElementById("nias-header") as HTMLScriptElement;
        
        if (existingScript && existingScript.src === scriptSource) {
            return;
        }

        document.getElementById("nias-header")?.remove();
        document.getElementById("egradjani")?.remove();
        loadScript(new URL(scriptSource));
    }, [scriptSource]);

    useEffect(() => {
        const root = document.documentElement;
        root.style.fontSize = `${accessibilityStyles.fontSize}%`;
        localStorage.setItem("accessibility", JSON.stringify(accessibilityStyles));
        window.dispatchEvent(
            new CustomEvent("accessibilityChange", { detail: accessibilityStyles })
        );
        const iframe = document.querySelector(".overlay-iframe") as HTMLIFrameElement | null;
        if (iframe?.contentWindow) {
            iframe.contentWindow.postMessage(
                {
                    type: "accessibilityChange",
                    payload: accessibilityStyles,
                },
                "*"
            );
        }
    }, [accessibilityStyles]);

    useEffect(() => {
        if (!niasReady) return;

        const impairedEl = document.querySelector<HTMLElement>("#impaired");
        const nonimpairedEl = document.querySelector<HTMLElement>("#nonimpaired");

        if (impairedEl) impairedEl.style.display = "none";
        if (nonimpairedEl) nonimpairedEl.style.display = "flex";

        document
            .querySelector("#nonimpaired")
            ?.addEventListener("click", lowVis.bind(null, setAccessibilityStyles, true));
        document
            .querySelector("#impaired")
            ?.addEventListener("click", lowVis.bind(null, setAccessibilityStyles, false));

        document
            .querySelector("#larger")
            ?.addEventListener("click", increaseFontSize.bind(null, setAccessibilityStyles));
        document
            .querySelector("#smaller")
            ?.addEventListener("click", decreaseFontSize.bind(null, setAccessibilityStyles));
        document
            .querySelector("#default")
            ?.addEventListener("click", resetFontSize.bind(null, setAccessibilityStyles));

        document
            .querySelector("#dyslexic")
            ?.addEventListener("click", setDyslexia.bind(null, setAccessibilityStyles, false));
        document
            .querySelector("#nondyslexic")
            ?.addEventListener("click", setDyslexia.bind(null, setAccessibilityStyles, true));
    }, [niasReady, setAccessibilityStyles]);

    useEffect(() => {
        if (!entityChangeDialogRendered) return;

        document
            .querySelector(".mfp-wrap")
            ?.addEventListener("click", () =>
                dispatch(changeShowChangeEntityDialog({ showChangeEntityDialog: false }))
            );
    }, [entityChangeDialogRendered, dispatch]);

    useEffect(() => {
        if (!niasReady) return;

        const logoutButton = document.querySelector<HTMLElement>('.log_out');
        if (!logoutButton) return;

        const handleLogoutClick = (event: Event) => {
            event.preventDefault();
            const logoutUrl = logoutButton.getAttribute('href');
            dispatch(logOff());
            
            setTimeout(() => {
                if (logoutUrl) {
                    window.location.href = logoutUrl;
                }
            }, 500);
        };

        logoutButton.addEventListener('click', handleLogoutClick);

        return () => {
            logoutButton?.removeEventListener('click', handleLogoutClick);
        };
    }, [niasReady, dispatch]);

    if (typeof window === "undefined") {
        return null;
    }

    return <div className="egradani_traka-holder" />;
};

export default NiasHeader;
